import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { User, Users, Film, Zap, Volume2, VolumeX, Dices, Map as MapIcon } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PageShell from "@/components/PageShell";
import ExcludePanel from "@/components/ExcludePanel";
import GuaranteePanel from "@/components/GuaranteePanel";
import WheelIcon from "@/components/WheelIcon";
import { useSettings } from "@/contexts/SettingsContext";
import { ATTACKERS, DEFENDERS, ATTACKER_ROLES, DEFENDER_ROLES } from "@/data/operators";
import { MAPS, getMapById } from "@/data/maps";

const TEAM_SIZES = [2, 3, 4, 5];

export const Settings = () => {
  const {
    mode,
    setMode,
    rollMode,
    setRollMode,
    teamSize,
    setTeamSize,
    soundOn,
    setSoundOn,
    excluded,
    toggleExcluded,
    clearExcluded,
    requiredRoles,
    toggleRequiredRole,
    loadoutToggles,
    toggleLoadout,
    selectedMapId,
    setSelectedMapId,
    selectedSite,
    setSelectedSite,
  } = useSettings();

  const currentMap = selectedMapId !== "random" && selectedMapId !== "none" ? getMapById(selectedMapId) : null;
  const siteOptions = currentMap ? currentMap.sites : [];

  const pools = useMemo(() => {
    const filter = (arr) => arr.filter((o) => !excluded.has(o.id));
    return { attacker: filter(ATTACKERS), defender: filter(DEFENDERS) };
  }, [excluded]);

  return (
    <PageShell>
      <section className="max-w-5xl mx-auto px-6 py-10 lg:py-12">
        <div className="flex items-end justify-between gap-6 mb-8">
          <div>
            <div
              className="text-[10px] text-neutral-500 tracking-[0.3em] uppercase"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              Configure Dispatch
            </div>
            <h1
              className="text-white text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none mt-1"
              style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
            >
              <span className="text-[#FFC800]">Settings</span>
            </h1>
          </div>
          <Link
            to="/randomise"
            data-testid="settings-to-randomise-btn"
            className="hidden sm:flex items-center gap-2 bg-[#FFC800] hover:bg-[#FFC800]/80 text-black font-black px-5 py-3 text-xs tracking-[0.3em] uppercase transition-all active:translate-y-[1px]"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            <Dices size={14} /> Randomise
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Mode */}
          <div className="border border-neutral-800 bg-neutral-950/60">
            <div
              className="border-b border-neutral-800 px-4 py-3 text-xs font-bold tracking-[0.3em] uppercase text-neutral-400"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              Mode
            </div>
            <div className="grid grid-cols-2">
              <button
                data-testid="mode-single-btn"
                onClick={() => setMode("single")}
                className={`flex items-center justify-center gap-2 py-4 text-xs font-bold tracking-[0.3em] uppercase transition-all ${
                  mode === "single"
                    ? "bg-[#FFC800] text-black"
                    : "text-neutral-400 hover:text-white hover:bg-neutral-900"
                }`}
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                <User size={14} /> Single
              </button>
              <button
                data-testid="mode-team-btn"
                onClick={() => setMode("team")}
                className={`flex items-center justify-center gap-2 py-4 text-xs font-bold tracking-[0.3em] uppercase transition-all border-l border-neutral-800 ${
                  mode === "team"
                    ? "bg-[#FFC800] text-black"
                    : "text-neutral-400 hover:text-white hover:bg-neutral-900"
                }`}
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                <Users size={14} /> Team
              </button>
            </div>
          </div>

          {/* Roll mode + sound */}
          <div className="border border-neutral-800 bg-neutral-950/60">
            <div
              className="border-b border-neutral-800 px-4 py-3 text-xs font-bold tracking-[0.3em] uppercase text-neutral-400"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              Roll Style
            </div>
            <div className="grid grid-cols-3">
              <button
                onClick={() => setRollMode("cinematic")}
                data-testid="roll-mode-cinematic-btn"
                className={`flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 py-3 px-2 text-[10px] sm:text-xs font-bold tracking-[0.15em] sm:tracking-[0.2em] uppercase transition-all ${
                  rollMode === "cinematic"
                    ? "bg-[#FFC800] text-black"
                    : "text-neutral-400 hover:text-white hover:bg-neutral-900"
                }`}
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                <Film size={14} /> Cinematic
              </button>
              <button
                onClick={() => setRollMode("instant")}
                data-testid="roll-mode-instant-btn"
                className={`flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 py-3 px-2 text-[10px] sm:text-xs font-bold tracking-[0.15em] sm:tracking-[0.2em] uppercase transition-all border-l border-neutral-800 ${
                  rollMode === "instant"
                    ? "bg-[#FFC800] text-black"
                    : "text-neutral-400 hover:text-white hover:bg-neutral-900"
                }`}
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                <Zap size={14} /> Instant
              </button>
              <button
                onClick={() => setRollMode("wheel")}
                data-testid="roll-mode-wheel-btn"
                className={`flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 py-3 px-2 text-[10px] sm:text-xs font-bold tracking-[0.15em] sm:tracking-[0.2em] uppercase transition-all border-l border-neutral-800 ${
                  rollMode === "wheel"
                    ? "bg-[#FFC800] text-black"
                    : "text-neutral-400 hover:text-white hover:bg-neutral-900"
                }`}
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                <WheelIcon size={14} /> Wheel
              </button>
            </div>
            <div className="border-t border-neutral-800 px-4 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-neutral-300">
                {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} className="text-neutral-500" />}
                <span
                  className="text-xs tracking-[0.2em] uppercase font-bold"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  Sound FX
                </span>
              </div>
              <Switch
                data-testid="sound-toggle"
                checked={soundOn}
                onCheckedChange={setSoundOn}
                className="data-[state=checked]:bg-[#FFC800]"
              />
            </div>
          </div>

          {/* Map & Site selection */}
          <div className="border border-neutral-800 bg-neutral-950/60 lg:col-span-2">
            <div
              className="border-b border-neutral-800 px-4 py-3 flex items-center justify-between"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              <span className="flex items-center gap-2 text-xs font-bold tracking-[0.3em] uppercase text-neutral-400">
                <MapIcon size={14} className="text-[#FFC800]" /> Map & Site
              </span>
              <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-600">
                Anything unselected rolls random
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-neutral-800">
              <div className="px-4 py-4">
                <div
                  className="text-[10px] tracking-[0.3em] uppercase font-bold text-neutral-400 mb-2"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  Map
                </div>
                <Select
                  value={selectedMapId}
                  onValueChange={(v) => {
                    setSelectedMapId(v);
                    setSelectedSite("random");
                  }}
                >
                  <SelectTrigger
                    data-testid="map-select-trigger"
                    className="bg-neutral-900 border-neutral-700 text-white"
                  >
                    <SelectValue placeholder="No Map" />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-950 border-neutral-700 text-white max-h-80">
                    <SelectItem value="none" data-testid="map-option-none">
                      No Map
                    </SelectItem>
                    <SelectItem value="random" data-testid="map-option-random">
                      Random Map
                    </SelectItem>
                    {MAPS.map((m) => (
                      <SelectItem key={m.id} value={m.id} data-testid={`map-option-${m.id}`}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="px-4 py-4">
                <div
                  className="text-[10px] tracking-[0.3em] uppercase font-bold text-neutral-400 mb-2"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  Bomb Site
                </div>
                <Select
                  value={selectedSite}
                  onValueChange={setSelectedSite}
                  disabled={!currentMap}
                >
                  <SelectTrigger
                    data-testid="site-select-trigger"
                    className="bg-neutral-900 border-neutral-700 text-white disabled:opacity-50"
                  >
                    <SelectValue placeholder={currentMap ? "Random Site" : "Pick a map first"} />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-950 border-neutral-700 text-white max-h-80">
                    <SelectItem value="random" data-testid="site-option-random">
                      Random Site
                    </SelectItem>
                    {siteOptions.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`site-option-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Loadout Suggestions */}
          <div className="border border-neutral-800 bg-neutral-950/60 lg:col-span-2">
            <div
              className="border-b border-neutral-800 px-4 py-3 flex items-center justify-between"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              <span className="text-xs font-bold tracking-[0.3em] uppercase text-neutral-400">
                Loadout Suggestions
              </span>
              <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-600">
                Pick any combo
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-neutral-800">
              <div className="divide-y divide-neutral-800">
                <LoadoutRow
                  iconUrl="https://customer-assets.emergentagent.com/job_r6-random-pick/artifacts/3pifml4p_a1030a95-197f-4766-b9fa-36bde045fab0_removalai_preview.png"
                  label="Primary Weapon"
                  hint="Random primary gun"
                  checked={loadoutToggles.primary}
                  onChange={() => toggleLoadout("primary")}
                  testId="loadout-toggle-primary"
                />
                <LoadoutRow
                  iconUrl="https://customer-assets.emergentagent.com/job_r6-random-pick/artifacts/1ru2i4p1_7d6e0b05-f4fe-4a14-ad8d-ad5e14bb9327_removalai_preview.png"
                  label="Secondary Weapon"
                  hint="Random sidearm"
                  checked={loadoutToggles.secondary}
                  onChange={() => toggleLoadout("secondary")}
                  testId="loadout-toggle-secondary"
                />
              </div>
              <div className="divide-y divide-neutral-800">
                <LoadoutRow
                  iconUrl="https://customer-assets.emergentagent.com/job_r6-random-pick/artifacts/hk90c13c_a90a0a89-9e1b-4760-8970-0c046c7b2437_removalai_preview%202.png"
                  label="Gadget"
                  hint="Random secondary gadget"
                  checked={loadoutToggles.gadget}
                  onChange={() => toggleLoadout("gadget")}
                  testId="loadout-toggle-gadget"
                />
              </div>
            </div>
          </div>

          {/* Team size — only when team mode */}
          {mode === "team" && (
            <div className="border border-neutral-800 bg-neutral-950/60 lg:col-span-2">
              <div
                className="border-b border-neutral-800 px-4 py-3 text-xs font-bold tracking-[0.3em] uppercase text-neutral-400"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                Squad Size
              </div>
              <div className="grid grid-cols-4">
                {TEAM_SIZES.map((n) => (
                  <button
                    key={n}
                    data-testid={`team-size-${n}-btn`}
                    onClick={() => setTeamSize(n)}
                    className={`py-5 text-xl font-black uppercase tracking-tight transition-all border-l first:border-l-0 border-neutral-800 ${
                      teamSize === n
                        ? "bg-[#FFC800] text-black"
                        : "text-neutral-400 hover:text-white hover:bg-neutral-900"
                    }`}
                    style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Guarantees — only in team mode */}
          {mode === "team" && (
            <>
              <GuaranteePanel
                side="attacker"
                roles={ATTACKER_ROLES}
                selected={requiredRoles.attacker}
                onToggle={(roleId) => toggleRequiredRole("attacker", roleId)}
                maxSlots={teamSize}
                pool={pools.attacker}
              />
              <GuaranteePanel
                side="defender"
                roles={DEFENDER_ROLES}
                selected={requiredRoles.defender}
                onToggle={(roleId) => toggleRequiredRole("defender", roleId)}
                maxSlots={teamSize}
                pool={pools.defender}
              />
            </>
          )}

          {/* Exclusions — always available, span full width */}
          <div className="lg:col-span-2">
            <ExcludePanel
              excluded={excluded}
              onToggle={toggleExcluded}
              onClear={clearExcluded}
            />
          </div>

          {/* Pool stats */}
          <div className="border border-neutral-800 bg-neutral-950/60 p-4 grid grid-cols-2 gap-4 text-center lg:col-span-2">
            <div>
              <div
                className="text-[10px] tracking-[0.3em] uppercase text-[#E67300]"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                Attacker Pool
              </div>
              <div
                className="text-3xl font-black text-white mt-1"
                style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
                data-testid="attacker-count"
              >
                {pools.attacker.length}
                <span className="text-neutral-600 text-lg">/{ATTACKERS.length}</span>
              </div>
            </div>
            <div>
              <div
                className="text-[10px] tracking-[0.3em] uppercase text-[#1F88E8]"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                Defender Pool
              </div>
              <div
                className="text-3xl font-black text-white mt-1"
                style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
                data-testid="defender-count"
              >
                {pools.defender.length}
                <span className="text-neutral-600 text-lg">/{DEFENDERS.length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile CTA */}
        <div className="mt-8 sm:hidden">
          <Link
            to="/randomise"
            data-testid="settings-to-randomise-btn-mobile"
            className="w-full flex items-center justify-center gap-2 bg-[#FFC800] hover:bg-[#FFC800]/80 text-black font-black px-5 py-4 text-xs tracking-[0.3em] uppercase transition-all"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            <Dices size={14} /> Randomise
          </Link>
        </div>
      </section>
    </PageShell>
  );
};

export default Settings;

const LoadoutRow = ({ iconUrl, label, hint, checked, onChange, testId }) => (
  <div className="flex items-center justify-between px-4 py-4">
    <div className="flex items-center gap-3 min-w-0">
      {iconUrl && (
        <img
          src={iconUrl}
          alt=""
          aria-hidden="true"
          draggable={false}
          className="w-9 h-9 shrink-0 select-none"
          style={{
            objectFit: "contain",
            mixBlendMode: "lighten",
          }}
        />
      )}
      <div className="min-w-0">
        <div
          className="text-xs lg:text-lg font-bold tracking-[0.25em] uppercase text-white"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          {label}
        </div>
        <div
          className="text-[10px] lg:text-sm text-neutral-500 tracking-wider mt-0.5 truncate"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          {hint}
        </div>
      </div>
    </div>
    <Switch
      data-testid={testId}
      checked={checked}
      onCheckedChange={onChange}
      className="data-[state=checked]:bg-[#FFC800]"
    />
  </div>
);
